import { Component, ReactNode, ErrorInfo } from 'react';
import MapFallback from './MapFallback';

interface MapErrorBoundaryProps {
  children: ReactNode;
  /** Bascule vers la vue liste existante (câblée par le parent, ex: HomePage). */
  onSwitchToList?: () => void;
}

interface MapErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary LOCAL à la carte (MapLibre).
 *
 * Contrairement au boundary global (src/components/common/ErrorBoundary.tsx,
 * monté dans src/index.tsx), celui-ci ne remplace PAS toute l'app : il isole
 * le crash à l'emplacement de la carte et affiche `MapFallback` à la place.
 * L'erreur est loggée en console.warn et ne remonte JAMAIS au boundary global
 * (React ne fait de toute façon pas remonter une erreur déjà catchée par un
 * boundary parent — mais on documente l'intention explicitement ici).
 *
 * ⚠️ Pas de remount infini : `hasError` reste `true` tant que le composant
 * n'est pas démonté/remonté explicitement par le parent (ex: via une prop
 * `key` qui change sur un contexte réellement différent). On ne réinitialise
 * jamais `hasError` automatiquement sur un re-render classique, ce qui évite
 * de retenter `new maplibregl.Map()` en boucle tant que la cause du crash
 * (GPU/WebGL) n'a pas changé.
 *
 * Voir docs/PRDs/PRD-map-error-boundary-fallback.md
 */
class MapErrorBoundary extends Component<MapErrorBoundaryProps, MapErrorBoundaryState> {
  state: MapErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): MapErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn('[MapErrorBoundary] Carte indisponible, fallback affiché:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <MapFallback onSwitchToList={this.props.onSwitchToList} />;
    }
    return this.props.children;
  }
}

export default MapErrorBoundary;
